// FILE: src/services/studyPlanRoadmap.js
//
// Pure derivation logic for the Study Plan Command Center:
//   • deriveRoadmap()      — the 7 certification milestones w/ real statuses
//   • computeDomainStats() — per-category practice totals from sessions
//   • deriveNextRisk()     — the single "Next up" recommendation
//
// Everything here is pure and unit-tested; no Supabase calls.

import { getExamConfig } from '../config/exams';

export const MILESTONE_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
  NEEDS_ATTENTION: 'needs_attention',
};

// Arc grouping ties the roadmap to PM Pixel's "Prepare · Practice · Pass".
export const ROADMAP_ARCS = [
  { id: 'prepare',  label: 'Prepare',  span: 3 },
  { id: 'practice', label: 'Practice', span: 2 },
  { id: 'pass',     label: 'Pass',     span: 2 },
];

const APPLICATION_KEYS = ['application', 'exam_paid', 'schedule_exam', 'location_choice'];

/**
 * @param {object} p
 * @param {object|null} p.plan            — active study_plans row (or null)
 * @param {object[]}    p.activities      — plan activities
 * @param {{done:number,total:number,pct:number}} p.reading
 * @param {object[]}    p.logistics       — loadLogistics() rows
 * @param {object|null} p.readiness       — computeReadiness() result
 * @param {number|null} p.daysUntil
 * @param {string}      p.examId
 * @returns {Array<{id,label,status,detail}>}
 */
export function deriveRoadmap({ plan, activities = [], reading = { done: 0, total: 0, pct: 0 }, logistics = [], readiness = null, daysUntil = null, examId }) {
  const S = MILESTONE_STATUS;
  const cfg = getExamConfig(examId) || {};
  const simTarget = cfg.target_full_sims || 3;

  // Exam Setup — a saved plan with a target date IS the setup.
  const setup = plan
    ? { status: S.COMPLETE, detail: 'Complete' }
    : { status: S.IN_PROGRESS, detail: 'Set your exam date' };

  // Application & Scheduling — from the logistics checklist subset.
  const appTasks = logistics.filter((t) => APPLICATION_KEYS.includes(t.key));
  const appDone = appTasks.filter((t) => t.completed).length;
  const appOverdue = appTasks.some((t) => t.overdue);
  let application;
  if (appTasks.length === 0) application = { status: S.NOT_STARTED, detail: '—' };
  else if (appOverdue) application = { status: S.NEEDS_ATTENTION, detail: 'Needs Attention' };
  else if (appDone >= appTasks.length) application = { status: S.COMPLETE, detail: 'Complete' };
  else if (appDone > 0) application = { status: S.IN_PROGRESS, detail: `${appDone} of ${appTasks.length}` };
  else application = { status: S.NOT_STARTED, detail: 'Not Started' };

  // Official Reading — self-attested checkoffs.
  let read;
  if (reading.total === 0) read = { status: S.NOT_STARTED, detail: 'No reading list' };
  else if (reading.pct >= 100) read = { status: S.COMPLETE, detail: 'Complete' };
  else if (reading.done > 0) read = { status: S.IN_PROGRESS, detail: `In Progress · ${reading.pct}%` };
  else read = { status: S.NOT_STARTED, detail: 'Not Started' };

  // Domain Practice — readiness phase-coverage input (measured).
  const covPct = Math.round(readiness?.inputs?.phase_coverage?.value ?? 0);
  const anyPractice = (readiness?.score ?? 0) > 0
    || activities.some((a) => ['practice', 'targeted'].includes(a.activity_type) && a.status === 'completed');
  let practice;
  if (covPct >= 100) practice = { status: S.COMPLETE, detail: 'Complete' };
  else if (anyPractice) practice = { status: S.IN_PROGRESS, detail: `In Progress · ${covPct}%` };
  else practice = { status: S.NOT_STARTED, detail: 'Not Started' };

  // Simulation Readiness — completed full sims vs the cert target.
  const simsDone = activities.filter((a) => a.activity_type === 'full' && a.status === 'completed').length;
  const sims = simsDone >= simTarget
    ? { status: S.COMPLETE, detail: `${simsDone} of ${simTarget} sims` }
    : { status: simsDone > 0 ? S.IN_PROGRESS : S.NOT_STARTED, detail: `${simsDone} of ${simTarget} sims` };

  // Final Review — the final-sprint window before the exam.
  let finalReview;
  if (daysUntil != null && daysUntil < 0) finalReview = { status: S.COMPLETE, detail: 'Complete' };
  else if (daysUntil != null && daysUntil < 14) finalReview = { status: S.IN_PROGRESS, detail: 'In Progress' };
  else finalReview = { status: S.NOT_STARTED, detail: 'Not Started' };

  // Exam Day.
  const examDay = {
    status: daysUntil != null && daysUntil <= 0 ? S.IN_PROGRESS : S.NOT_STARTED,
    detail: plan?.target_exam_date || '—',
  };

  return [
    { id: 'setup',       label: 'Exam Setup',               ...setup },
    { id: 'application', label: 'Application & Scheduling', ...application },
    { id: 'reading',     label: 'Official Reading',         ...read },
    { id: 'practice',    label: 'Domain Practice',          ...practice },
    { id: 'sims',        label: 'Simulation Readiness',     ...sims },
    { id: 'final',       label: 'Final Review',             ...finalReview },
    { id: 'exam',        label: 'Exam Day',                 ...examDay },
  ];
}

/** First milestone that still needs work — drives the hero phase chip. */
export function currentMilestone(roadmap) {
  return roadmap.find((m) => m.status === MILESTONE_STATUS.NEEDS_ATTENTION)
    || roadmap.find((m) => m.status === MILESTONE_STATUS.IN_PROGRESS)
    || roadmap.find((m) => m.status === MILESTONE_STATUS.NOT_STARTED)
    || roadmap[roadmap.length - 1];
}

/**
 * Per-category practice stats from normalized readiness sessions
 * (phaseBreakdown aggregation — question_attempts-derived upstream).
 * @returns {Array<{ category, weightPct, total, correct, accuracy|null }>}
 */
export function computeDomainStats(sessions, examId) {
  const cfg = getExamConfig(examId) || {};
  const dist = cfg.categoryDistribution || {};
  const agg = {};
  for (const s of sessions || []) {
    const pb = s.phaseBreakdown || {};
    for (const k of Object.keys(pb)) {
      const e = pb[k] || {};
      if (!agg[k]) agg[k] = { total: 0, correct: 0 };
      agg[k].total += Number(e.total) || 0;
      agg[k].correct += Number(e.correct) || 0;
    }
  }
  return Object.keys(dist).map((category) => {
    const a = agg[category] || { total: 0, correct: 0 };
    return {
      category,
      weightPct: dist[category],
      total: a.total,
      correct: a.correct,
      accuracy: a.total >= 5 ? Math.round((100 * a.correct) / a.total) : null,
    };
  });
}

/**
 * Single "Next up" recommendation. Precedence: overdue logistics >
 * reading gap > weakest domain > simulation gap > steady-state.
 */
export function deriveNextRisk({ logistics = [], reading = { pct: 0, total: 0 }, domainStats = [], activities = [], daysUntil = null, examId }) {
  const overdue = logistics.find((t) => t.overdue);
  if (overdue) {
    return `${overdue.title} — recommended by ${overdue.dueDate}. Don't let logistics block exam day.`;
  }
  if (reading.total > 0 && reading.pct < 50 && daysUntil != null && daysUntil < 28) {
    return `Official reading is at ${reading.pct}% with ${daysUntil} days left — schedule the remaining references this week.`;
  }
  const weak = domainStats
    .filter((d) => d.accuracy != null && d.accuracy < 70)
    .sort((a, b) => a.accuracy - b.accuracy)[0];
  if (weak) {
    return `${weak.category} is your thinnest area (${weak.accuracy}%) — targeted practice there moves readiness fastest.`;
  }
  const cfg = getExamConfig(examId) || {};
  const simTarget = cfg.target_full_sims || 3;
  const simsDone = activities.filter((a) => a.activity_type === 'full' && a.status === 'completed').length;
  if (simsDone < simTarget && daysUntil != null && daysUntil <= 21) {
    return `${simsDone} of ${simTarget} full simulations done with ${daysUntil} days left — protect the scheduled sim days.`;
  }
  return 'You are on track — keep following the weekly plan.';
}
